<div class="wrapper flex flex-col justify-between">
  <div class="heading text-type-2 mb-[20px] flex justify-between items-center">
    {{ claim?.claim_number || 'Claim Detail' }}
    @if (canEditClaim()) {
      <button (click)="editClaim()" class="edit-icon-button" aria-label="Edit claim" matTooltip="Edit claim">
        <mat-icon svgIcon="pencil-outline" />
      </button>
    }
  </div>

  <mat-tab-group class="contents-tab-group flex-1" [selectedIndex]="selectedTabIndex" (selectedIndexChange)="selectedTabIndex = $event">
    <mat-tab label="Details">
      <div class="details-content pt-[20px]">
        <!-- Status -->
        <div class="row-details flex flex-col gap-[4px] mb-[20px]">
          <div class="label w-full">Status</div>
          <div class="value w-full">
            @if (claim?.status && statusMap[claim.status]) {
              <div
                [ngClass]="[statusMap[claim.status].bgColor, statusMap[claim.status].textColor]"
                class="status-chip px-[14px] rounded-full w-fit">
                {{ statusMap[claim.status].label }}
              </div>
            }
          </div>
        </div>

        <!-- Warranty -->
        @if (claim?.warranty?.name) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Warranty</div>
            <div class="value w-full text-type-11">{{ claim.warranty.name }}</div>
          </div>
        }

        <!-- Title -->
        <div class="row-details flex flex-col gap-[4px] mb-[20px]">
          <div class="label w-full">Title</div>
          <div class="value w-full text-type-11">{{ claim?.title || 'N/A' }}</div>
        </div>

        <!-- Description -->
        @if (claim?.description) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Description</div>
            <div class="value w-full text-type-11">{{ claim.description }}</div>
          </div>
        }

        <!-- Site -->
        @if (claim?.site?.name) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Site</div>
            <div class="value w-full text-type-11">{{ claim.site.name }}</div>
          </div>
        }

        <!-- Structure Type -->
        @if (claim?.structure_type) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Structure type</div>
            <div class="value w-full text-type-11">{{ claim.structure_type }}</div>
          </div>
        }

        <!-- Type (Claim type) -->
        <div class="row-details flex flex-col gap-[4px] mb-[20px]">
          <div class="label w-full">Claim type</div>
          <div class="value w-full text-type-11">{{ claim?.claim_type | titlecase }}</div>
        </div>

        <!-- Defect Type -->
        @if (claim?.defect_type) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Defect type</div>
            <div class="value w-full text-type-11">{{ claim.defect_type }}</div>
          </div>
        }

        <!-- Severity -->
        <div class="row-details flex flex-col gap-[4px] mb-[20px]">
          <div class="label w-full">Severity</div>
          <div class="value w-full">
            @if (claim?.severity && severityMap[claim.severity]) {
              <div
                [ngClass]="[severityMap[claim.severity].bgColor, severityMap[claim.severity].textColor]"
                class="status-chip px-[14px] rounded-full w-fit">
                {{ severityMap[claim.severity].label }}
              </div>
            }
          </div>
        </div>

        <!-- Need by date -->
        @if (claim?.need_by_date) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Need by date</div>
            <div class="value w-full text-type-11">{{ claim.need_by_date | date:'MMM d, y' }}</div>
          </div>
        }

        <!-- Proposed Solution -->
        @if (claim?.proposed_solution) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Proposed solution</div>
            <div class="value w-full text-type-11">{{ claim.proposed_solution }}</div>
          </div>
        }

        <!-- Location map -->
        @if (claim?.latitude && claim?.longitude) {
          <app-location-section
            [isEditable]="false"
            [latitude]="claim.latitude"
            [longitude]="claim.longitude"
            [mapFeatureLink]="claim.map_feature_link ?? null"
            class="mb-[20px]"
          />
        }
        <!-- Design context (spec §12): the location slot above shows the capture. -->
        @if (claim?.map_feature_link) {
          <app-design-context
            class="block mb-[20px]"
            data-testid="warranty-claim-detail-design-context"
            [link]="claim.map_feature_link"
            [collapseWhenCaptured]="!!(claim.latitude && claim.longitude)"
            [editable]="false"
            [projectId]="projectId"
          />
        }

        <!-- Attachments -->
        @if (claim?.pictures?.length || claim?.attachments?.length) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full uppercase">Attachments</div>
            <div class="pictures-row flex flex-wrap gap-[12px] mt-[8px]">
              @for (picture of (claim?.pictures || []).concat(claim?.attachments || []); track picture.id) {
                <div class="picture-block">
                  @if (appService.getAttachmentFileType(picture) === 'video') {
                    <div
                      (click)="appService.openCarousel($event, (claim?.pictures || []).concat(claim?.attachments || []), picture, false)"
                      class="video-thumb"
                    >
                      @if (picture.preview_gif_url || picture.thumb_url) {
                        <img [src]="picture.preview_gif_url || picture.thumb_url" alt="Video preview" />
                      } @else if (picture.original_url) {
                        <video [src]="picture.original_url + '#t=0.1'" muted playsinline preload="metadata"></video>
                      }
                      <mat-icon class="play-icon">play_circle</mat-icon>
                    </div>
                  } @else {
                    <app-image
                      (click)="appService.openCarousel($event, (claim?.pictures || []).concat(claim?.attachments || []), picture, false)"
                      [url]="picture.thumb_url || picture.original_url"
                      class="each-picture"
                    />
                  }
                </div>
              }
            </div>
          </div>
        }

        <!-- Linked Issues -->
        @if (linkedIssuesLoading) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Linked Issues</div>
            <div class="value w-full text-type-14 text-[#60698B]">Loading...</div>
          </div>
        } @else if (linkedIssues.length) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full flex items-center gap-[8px]">
              Linked Issues
              <span class="linked-issues-count-badge">{{ linkedIssues.length }}</span>
            </div>
            <div class="linked-issues-list mt-[8px]">
              @for (issue of linkedIssues; track issue.id) {
                <div class="linked-issue-item flex flex-col gap-[4px] mb-[8px] cursor-pointer"
                     (click)="issue._expanded = !issue._expanded">
                  <div class="flex items-center justify-between">
                    <div class="text-type-11 font-medium">{{ issue.title }}</div>
                    <mat-icon class="expand-icon text-[#60698B]" style="font-size:18px;width:18px;height:18px">
                      {{ issue._expanded ? 'expand_less' : 'expand_more' }}
                    </mat-icon>
                  </div>
                  <div class="flex items-center gap-[8px] flex-wrap">
                    <span class="linked-issue-type-badge">{{ issue.type || 'Issue' }}</span>
                    <span class="linked-issue-status-badge">{{ issueStatusText(issue.status) }}</span>
                    <span class="text-type-14 text-[#60698B]">{{ issue.created_by_first_name }} {{ issue.created_by_last_name }}</span>
                  </div>
                  @if (issue._expanded) {
                    @if (issue.description) {
                      <div class="text-type-14 text-[#60698B] mt-[4px]">{{ issue.description }}</div>
                    }
                    @if (issue.pictures?.length) {
                      <div class="issue-pictures flex gap-[6px] mt-[6px] flex-wrap">
                        @for (pic of issue.pictures; track pic.id) {
                          <img [src]="pic.thumb_url || pic.original_url" class="issue-thumbnail" alt="Issue photo" />
                        }
                      </div>
                    }
                    @if (!issue.description && !issue.pictures?.length) {
                      <div class="text-type-14 text-[#60698B] mt-[4px] italic">No additional details</div>
                    }
                  }
                </div>
              }
            </div>
          </div>
        }

        <!-- Created date -->
        @if (claim?.created_at) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Created</div>
            <div class="value w-full text-type-11">{{ claim.created_at | date:'MMM d, y' }}</div>
          </div>
        }

        <!-- Filed By -->
        @if (claim?.createdBy) {
          <div class="row-details flex flex-col gap-[4px] mb-[20px]">
            <div class="label w-full">Filed by</div>
            <div class="value w-full text-type-11">
              {{ claim.createdBy?.first_name }} {{ claim.createdBy?.last_name }}
            </div>
          </div>
        }

        <!-- Actions dropdown -->
        @if (actionOptions.length) {
          <hr class="hr-line" />
          <app-dropdown
            [options]="actionOptions"
            [parentFormGroup]="actionForm"
            class="w-full mb-[20px]"
            controlName="action"
            placeholder="Take action on this claim"
            label="actions"
          />

          <!-- Notes text area (shown when an action is selected) -->
          @if (actionForm.get('action')?.value) {
            <app-text-area
              [parentFormGroup]="actionForm"
              class="w-full mb-[20px]"
              controlName="notes"
              label="Comments"
            />

            <!-- Attachments section with + add tile (matches RFI pattern) -->
            <app-pictures-section-with-tags
              (onDelete)="deleteActionPicture($event)"
              [add]="true"
              [pictures]="actionMediaFiles"
              [tags]="'actions-attachments'"
              class="pictures-section mb-[40px]"
              label="Attachments"
            />
          }
        }
      </div>
    </mat-tab>

    <mat-tab label="Discussion">
      <div class="discussion-content pt-[20px]">
        @if (workflowHistory.length) {
          @for (entry of workflowHistory; track entry.date) {
            <div class="workflow-entry mb-[20px]">
              <div class="flex flex-row items-start gap-[12px]">
                <div class="workflow-icon">
                  <mat-icon class="text-[#36AB19]">check_circle</mat-icon>
                </div>
                <div class="flex flex-col flex-1">
                  <div class="entry-action text-type-8">{{ entry.action }}</div>
                  <div class="flex flex-row items-center gap-[8px] mt-[8px]">
                    <div class="avatar">{{ entry.actor_initials }}</div>
                    <div class="flex flex-col">
                      <span class="text-type-11">{{ entry.actor_name }}</span>
                    </div>
                  </div>
                  <div class="text-type-14 text-[#60698B] mt-[4px]">
                    {{ entry.date | date:'MMM d, y' }} &bull; {{ entry.date | date:'h:mm a' }}
                  </div>
                  @if (entry.notes) {
                    <div class="comment-section mt-[12px]">
                      <div class="comment-label text-type-13 uppercase">Comment:</div>
                      <div class="comment-text text-type-11 mt-[4px]">{{ entry.notes }}</div>
                    </div>
                  }
                  @if (entry.attachments?.length) {
                    <div class="comment-section mt-[12px]">
                      <div class="comment-label text-type-13 uppercase">Media:</div>
                      <div class="pictures-row flex flex-wrap gap-[12px] mt-[8px]">
                        @for (attachment of entry.attachments; track attachment.id) {
                          <div class="picture-block">
                            @if (appService.getAttachmentFileType(attachment) === 'video') {
                              <div
                                (click)="appService.openCarousel($event, entry.attachments, attachment, false)"
                                class="video-thumb"
                              >
                                @if (attachment.preview_gif_url || attachment.thumb_url) {
                                  <img [src]="attachment.preview_gif_url || attachment.thumb_url" alt="Video preview" />
                                } @else if (attachment.original_url) {
                                  <video [src]="attachment.original_url + '#t=0.1'" muted playsinline preload="metadata"></video>
                                }
                                <mat-icon class="play-icon">play_circle</mat-icon>
                              </div>
                            } @else {
                              <app-image
                                (click)="appService.openCarousel($event, entry.attachments, attachment, false)"
                                [url]="attachment.thumb_url || attachment.original_url"
                                class="each-picture"
                              />
                            }
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
              <hr class="entry-divider" />
            </div>
          }
        } @else {
          <div class="no-data text-type-14 text-[#60698B] text-center py-[40px]">No activity yet.</div>
        }
      </div>
    </mat-tab>
  </mat-tab-group>

  <!-- Submit button at bottom -->
  @if (actionOptions.length && selectedTabIndex === 0) {
    <div class="action-section mb-[5px] flex flex-row gap-[20px] w-full">
      <app-primary-button (handleClick)="executeAction()" [isDisabled]="!actionForm.get('action')?.value" class="flex-1">
        Submit
      </app-primary-button>
    </div>
  }
</div>
